/*
 * Copyright Mimic Networks, Inc. 2024.
 */

import { Grid } from 'antd';

import { Pagination } from '@/client';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { ErrorIcon, WarningIcon } from '@/primitives/Icons';
import { tokens } from '@/theme';
import { paginationRange } from '@/utils/pagination';

export type PaginationInfoParams = {
  pagination?: Pagination;
  primaryAction?: React.ReactNode;
  selectedIds?: string[];
  selectionWarning?: string;
  selectionError?: string;
};

const defaultStyle: React.CSSProperties = {
  fontSize: '0.875rem',
  minHeight: '52px',
  padding: '0 16px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  backgroundColor: tokens.color.surface.surfaceHigh,
  borderBottom: 'none',
  borderWidth: '1px 1px 0 1px',
  borderStyle: 'solid',
  borderColor: 'rgba(25, 25, 25, 0.06)',
};

const mobileStyle: React.CSSProperties = {
  ...defaultStyle,
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: 12,
  padding: '8px 16px',
};

const mobileWarningStyle: React.CSSProperties = {
  ...mobileStyle,
  backgroundColor: 'rgba(250, 246, 207, 0.7)',
  borderColor: 'rgba(250, 200, 61, 0.3)',
};

const mobileErrorStyle: React.CSSProperties = {
  ...mobileStyle,
  backgroundColor: 'rgba(248, 233, 229, 0.7)',
  borderColor: 'rgba(161, 53, 23, 0.3)',
};

const warningStyle: React.CSSProperties = {
  ...defaultStyle,
  backgroundColor: 'rgba(250, 246, 207, 0.7)',
  borderColor: 'rgba(250, 200, 61, 0.3)',
};

const errorStyle: React.CSSProperties = {
  ...defaultStyle,
  backgroundColor: 'rgba(248, 233, 229, 0.7)',
  borderColor: 'rgba(161, 53, 23, 0.3)',
};

const { useBreakpoint } = Grid;

export function PaginationInfo({
  pagination,
  primaryAction,
  selectedIds,
  selectionWarning,
  selectionError,
}: PaginationInfoParams) {
  const { t } = useMimicTranslation('pagination');
  const screens = useBreakpoint();

  if (!pagination) {
    return null;
  }

  const { start, end, total } = paginationRange(pagination);
  const leftLabel = selectedIds?.length
    ? t('selectedItems', { selected: selectedIds.length })
    : t('summary', { start, end, total });

  // define style based on selectionWarning or selectionError
  let style = defaultStyle;

  switch (true) {
    case screens.md && !!selectionWarning:
      style = warningStyle;
      break;
    case screens.md && !!selectionError:
      style = errorStyle;
      break;
    case !!selectionWarning:
      style = mobileWarningStyle;
      break;
    case !!selectionError:
      style = mobileErrorStyle;
      break;
    default:
      style = defaultStyle;
  }

  return screens.md ? (
    <div data-testid="pagination-info" style={style}>
      <div style={{ display: 'flex', gap: 16 }}>
        <div style={{ fontWeight: '600' }}>{leftLabel}</div>
        {selectionWarning && !selectionError && (
          <div style={{ display: 'flex', gap: 4 }}>
            <WarningIcon />
            {selectionWarning}
          </div>
        )}
        {selectionError && (
          <div style={{ display: 'flex', gap: 4, color: 'rgba(161, 53, 23, 1)' }}>
            <ErrorIcon />
            {selectionError}
          </div>
        )}
      </div>
      {primaryAction}
    </div>
  ) : (
    <div data-testid="pagination-info" style={style}>
      {selectionWarning && !selectionError && (
        <div style={{ display: 'flex', gap: 4 }}>
          <WarningIcon />
          {selectionWarning}
        </div>
      )}
      {selectionError && (
        <div style={{ display: 'flex', gap: 4, color: 'rgba(161, 53, 23, 1)' }}>
          <ErrorIcon />
          {selectionError}
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
        <div style={{ fontWeight: '600' }}>{leftLabel}</div>
        {primaryAction}
      </div>
    </div>
  );
}
